import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { authAPI } from '@/utils/api';
import { saveAuth } from '@/utils/auth';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const AuthPage = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    age_verified: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await authAPI.login({
          email: formData.email,
          password: formData.password
        });
        saveAuth(response.data.token, response.data.user);
        toast.success('Erfolgreich angemeldet!');
        navigate('/dashboard');
      } else {
        if (!formData.age_verified) {
          toast.error('Du musst bestätigen, dass du 18+ bist');
          setLoading(false);
          return;
        }
        const response = await authAPI.register(formData);
        saveAuth(response.data.token, response.data.user);
        toast.success('Konto erfolgreich erstellt!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-4 text-gray-400 hover:text-white"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
        </Button>

        <div className="glass-effect rounded-xl p-8" data-testid="auth-form">
          <h1 className="text-3xl font-bold mb-6 text-center">
            {isLogin ? 'Anmelden' : 'Registrieren'}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-gray-400 mb-2 block">
                E-Mail
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="bg-[#121212] border-white/10 focus:border-[#00BFA6] text-white"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-400 mb-2 block">
                Passwort
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="bg-[#121212] border-white/10 focus:border-[#00BFA6] text-white"
                required
                data-testid="password-input"
              />
            </div>

            {!isLogin && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="age-verify"
                  checked={formData.age_verified}
                  onCheckedChange={(checked) => setFormData({...formData, age_verified: checked})}
                  data-testid="age-verification-checkbox"
                />
                <Label htmlFor="age-verify" className="text-sm text-gray-300 cursor-pointer">
                  Ich bestätige, dass ich 18 Jahre oder älter bin
                </Label>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00BFA6] hover:bg-[#009E8A] text-black font-bold py-3 rounded-lg neon-glow"
              data-testid="submit-auth-btn"
            >
              {loading ? 'Lädt...' : (isLogin ? 'Anmelden' : 'Registrieren')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#00BFA6] hover:underline"
              data-testid="toggle-auth-mode-btn"
            >
              {isLogin ? 'Noch kein Konto? Registrieren' : 'Bereits registriert? Anmelden'}
            </button>
          </div>

          <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-xs text-gray-300 text-center">
              ⚠️ Nur für Erwachsene ab 18 Jahren. Wetten können süchtig machen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
